import React, { useState } from 'react';
import { MealPlanItem, Recipe } from '@/types/api';
import { MealSlot } from './MealSlot';
import { Badge } from '@/components/ui/badge';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';

interface DayServiceContainerProps {
  day: Date;
  service: 'comida' | 'cena';
  mealPlanItem?: MealPlanItem;
  onEditServings?: (item: MealPlanItem) => void;
  onRemoveItem?: (item: MealPlanItem) => void;
  onReplaceRecipe?: (item: MealPlanItem) => void;
  onSlotSelect?: (day: Date, service: 'comida' | 'cena') => void;
  onViewRecipe?: (recipe: Recipe) => void;
  onToggleFavorite?: (recipe: Recipe) => void;
  isFavorite?: (recipeId: string) => boolean;
  disabled?: boolean;
}

export function DayServiceContainer({
  day,
  service,
  mealPlanItem,
  onEditServings,
  onRemoveItem,
  onReplaceRecipe,
  onSlotSelect,
  onViewRecipe,
  onToggleFavorite,
  isFavorite,
  disabled = false
}: DayServiceContainerProps) {
  // Por defecto: expandido si tiene receta, colapsado si está vacío
  const [isExpanded, setIsExpanded] = useState(!!mealPlanItem);
  
  // Auto-expandir cuando se añade una receta
  React.useEffect(() => {
    if (mealPlanItem && !isExpanded) {
      setIsExpanded(true);
    }
  }, [mealPlanItem, isExpanded]);
  
  const hasRecipe = !!mealPlanItem;
  
  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  const serviceConfig = {
    comida: {
      icon: '🥗',
      label: 'Comida',
      bgColor: 'bg-orange-50',
      borderColor: 'border-orange-200',
      textColor: 'text-orange-700'
    },
    cena: {
      icon: '🍽️',
      label: 'Cena',
      bgColor: 'bg-blue-50',
      borderColor: 'border-blue-200',
      textColor: 'text-blue-700'
    }
  };

  const config = serviceConfig[service];

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden bg-white shadow-sm">
      {/* Service Header - Clickeable */}
      <button
        onClick={toggleExpanded}
        className={cn(
          "w-full px-3 py-2 flex items-center justify-between transition-colors",
          config.bgColor,
          "hover:opacity-80 active:opacity-60"
        )}
        disabled={disabled}
      >
        <div className="flex items-center gap-2">
          <span className="text-sm">{config.icon}</span>
          <span className={cn("text-sm font-medium", config.textColor)}>
            {config.label}
          </span>
          {hasRecipe && (
            <Badge variant="secondary" className="text-xs h-5 px-1.5">
              Planificado
            </Badge>
          )}
        </div>
        
        <div className="flex items-center gap-1">
          {isExpanded ? (
            <ChevronDown className={cn("h-4 w-4", config.textColor)} />
          ) : (
            <ChevronRight className={cn("h-4 w-4", config.textColor)} />
          )}
        </div>
      </button>

      {/* Service Content - Colapsable */}
      <div 
        className={cn(
          "transition-all duration-300 ease-in-out overflow-hidden",
          isExpanded ? "max-h-96 opacity-100" : "max-h-0 opacity-0"
        )}
      >
        <div className="p-3">
          <MealSlot
            day={day}
            service={service}
            mealPlanItem={mealPlanItem}
            onEditServings={onEditServings}
            onRemoveItem={onRemoveItem}
            onReplaceRecipe={onReplaceRecipe}
            onSlotSelect={onSlotSelect}
            onViewRecipe={onViewRecipe}
            onToggleFavorite={onToggleFavorite}
            isFavorite={isFavorite}
            disabled={disabled}
            className="border-0 shadow-none bg-transparent min-h-[90px]"
          />
        </div>
      </div>
    </div>
  );
}
